// The account's background bulk uploads: the running one with its progress, the ones waiting (position, estimated
// wait, cancel) and finished results not opened yet. All texts are rendered as text, never as HTML.
import React, { useRef } from "react";
import { Box, Button, LinearProgress } from "@mui/material";

const PHASES = {
  queued: "Waiting to start",
  planning: "Checking the file",
  applying: "Saving shops",
  finalizing: "Finishing",
};
const RESULT_STATES = {
  completed: "Completed",
  partial: "Completed with problems",
  failed: "Failed",
};

// A worker starts an upload with nothing ahead within seconds; after this long it is waiting for a worker
const STARTING_MS = 60000;

const waitText = ({ queuePosition, estimatedWaitMinutes }, waitingMs) => {
  if (!(queuePosition > 0)) return waitingMs < STARTING_MS ? "Starting…" : "Waiting to start";
  const parts = ["Waiting to start"];
  if (queuePosition > 0) parts.push(`${queuePosition} upload${queuePosition === 1 ? "" : "s"} ahead`);
  if (estimatedWaitMinutes) parts.push(`about ${estimatedWaitMinutes} min (estimate)`);
  return parts.join(" · ");
};

const fileText = (name, rows) => `${name || "upload"}${rows ? ` (${rows} rows)` : ""}`;

const formatTime = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : "";
};

const BulkUploadJobs = ({ current, reconnecting, message, onCancel, onOpenResult }) => {
  const { active, queued = [], unacknowledged = [], more } = current || {};
  // When this page first saw each waiting upload (the browser's own clock, so a wrong PC clock does not matter)
  const firstSeen = useRef(new Map());
  queued.forEach(({ jobId }) => { if (!firstSeen.current.has(jobId)) firstSeen.current.set(jobId, Date.now()); });
  const inProgress = Boolean(active) || queued.length > 0;
  if (!inProgress && !unacknowledged.length && !message && !reconnecting) return null;
  const progress = active?.progress;
  const percent = progress?.total ? Math.round((progress.processed / progress.total) * 100) : 0;

  return (
    <Box className="bulk-upload-jobs" data-testid="bulk-upload-jobs">
      {reconnecting && <p className="bulk-upload-jobs__note">Reconnecting…</p>}
      {message && <p className="bulk-upload-jobs__note err" role="alert">{message}</p>}

      {active && (
        <div className="bulk-upload-jobs__item">
          <div className="bulk-upload-jobs__head">
            <span className="bulk-upload-jobs__status">{PHASES[active.state] || "Working"}</span>
            {progress?.total > 0 && <span className="bulk-upload-jobs__percent">{`${percent}%`}</span>}
          </div>
          <LinearProgress className="bulk-upload-jobs__bar" variant={progress?.total ? "determinate" : "indeterminate"} value={percent} />
          <p className="bulk-upload-jobs__file">{fileText(active.originalname, progress?.total)}</p>
          {progress?.total > 0 && (
            <p className="bulk-upload-jobs__counts">
              {`${progress.processed} of ${progress.total} rows · ${progress.inserted} added · ${progress.updated} updated · ${progress.skipped} unchanged · ${progress.failed} failed`}
            </p>
          )}
        </div>
      )}

      {queued.map((job) => {
        const status = waitText(job, Date.now() - firstSeen.current.get(job.jobId));
        return (
          <div className="bulk-upload-jobs__item d-flex align-center" key={job.jobId}>
            <div className="w-100">
              <div className="bulk-upload-jobs__head"><span className="bulk-upload-jobs__status">{status}</span></div>
              {/* A moving bar only while a worker is picking it up; a waiting upload shows no fake activity */}
              {status === "Starting…" && <LinearProgress className="bulk-upload-jobs__bar" variant="indeterminate" />}
              <p className="bulk-upload-jobs__file">{fileText(job.originalname, job.rowCount)}</p>
            </div>
            {onCancel && <Button className="cancel-button" onClick={() => onCancel(job.jobId)}>Cancel</Button>}
          </div>
        );
      })}

      {inProgress && (
        <p className="bulk-upload-jobs__note">
          Please wait while your shops are uploaded. You can leave this page: the upload continues and its result opens
          here when you come back.
        </p>
      )}

      {unacknowledged.length > 0 && (
        <div className="bulk-upload-jobs__finished">
          <p className="bulk-upload-jobs__title">Finished uploads</p>
          {unacknowledged.map((job) => (
            <div className="bulk-upload-jobs__item d-flex align-center" key={job.jobId}>
              <div className="w-100">
                <p>{job.originalname || "upload"}</p>
                <p className="bulk-upload-jobs__counts">
                  {[RESULT_STATES[job.state] || job.state, job.userName, formatTime(job.finishedAt)].filter(Boolean).join(" · ")}
                </p>
              </div>
              {onOpenResult && <Button className="secondary_button" onClick={() => onOpenResult(job.jobId)}>View result</Button>}
            </div>
          ))}
          {more && <p className="bulk-upload-jobs__note">Older results are not shown.</p>}
        </div>
      )}
    </Box>
  );
};

export default BulkUploadJobs;
